import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import * as XLSX from "xlsx";
import { Search, X, Filter, ChevronDown, ChevronRight, Plus, Trash2, Download, PackageCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { FormulaInput } from "@/components/FormulaInput";
import {
  getSuppliers, getVendorCalls, addVendorCall, updateVendorCall, deleteVendorCall, formatApiError,
} from "@/lib/api";
import { slug, unitOf, formatDate, evalFormula, deriveFabricCodes, itemMatchesQuery, SWATCHES } from "@/lib/fabricData";

const fmt = (n) => Number(n || 0).toLocaleString(undefined, { maximumFractionDigits: 2 });
const norm = (s) => String(s || "").replace(/\s+/g, "").toLowerCase();
const keyOf = (f, c) => `${f}|${c}`;

// Excel-style multi-select column filter dropdown.
function ColumnFilter({ label, align = "left", values, selected, onToggle, onClear }) {
  const [open, setOpen] = useState(false);
  const active = selected.size > 0;
  return (
    <div className="relative inline-flex items-center gap-1">
      <span>{label}</span>
      <button type="button" onClick={() => setOpen((o) => !o)} data-testid={`ls-filter-${slug(label)}`}
        className={`p-0.5 ${active ? "text-emerald-400" : "text-white/30 hover:text-white"}`} title={`Filter ${label}`}>
        <Filter className="h-3 w-3" />
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
          <div className={`absolute top-5 ${align === "right" ? "right-0" : "left-0"} z-50 w-56 max-h-64 overflow-auto bg-[#141414] border border-white/15 p-2 shadow-2xl shadow-black/60 normal-case tracking-normal`}>
            <div className="flex items-center justify-between mb-1.5 pb-1.5 border-b border-white/10">
              <span className="text-[10px] text-white/40">{selected.size} selected</span>
              <button type="button" onClick={onClear} data-testid={`ls-filter-clear-${slug(label)}`} className="text-[10px] uppercase tracking-wider text-white/60 hover:text-white">Clear</button>
            </div>
            {values.length === 0 && <div className="text-[11px] text-white/40 py-1">No values</div>}
            {values.map((v) => (
              <label key={v || "(blank)"} className="flex items-center gap-2 py-1 text-xs text-white/90 cursor-pointer hover:text-white">
                <input type="checkbox" checked={selected.has(v)} onChange={() => onToggle(v)} className="accent-white h-3.5 w-3.5 shrink-0" />
                <span className="break-words">{v === "" ? "(blank)" : v}</span>
              </label>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

const emptyFilters = { fabric: new Set(), color: new Set(), available: new Set(), min: new Set(), pending: new Set() };

export function LowStockTab({ token, stock, fabrics, colors, onStockRefresh }) {
  const [threshold, setThreshold] = useState(5);
  const [query, setQuery] = useState("");
  const [suppliers, setSuppliers] = useState([]);
  const [calls, setCalls] = useState([]);
  const [expanded, setExpanded] = useState(null);
  const [selected, setSelected] = useState(new Set());
  const [colFilters, setColFilters] = useState(emptyFilters);
  const [limit, setLimit] = useState(20);
  const [drafts, setDrafts] = useState({}); // per-row new-entry draft

  const codeMap = useMemo(() => deriveFabricCodes(stock, fabrics), [stock, fabrics]);
  const colorByName = useMemo(() => Object.fromEntries((colors || []).map((c) => [c.name, c])), [colors]);

  const load = async () => {
    try {
      const [sup, vc] = await Promise.all([getSuppliers(token), getVendorCalls(token)]);
      setSuppliers(sup || []);
      setCalls(vc || []);
    } catch (e) { /* ignore */ }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [token]);
  useEffect(() => { setLimit(20); }, [query, threshold]);

  const callsByKey = useMemo(() => {
    const m = {};
    (calls || []).forEach((c) => { const k = keyOf(c.fabric, c.color); (m[k] = m[k] || []).push(c); });
    return m;
  }, [calls]);
  const totalPending = (f, c) => (callsByKey[keyOf(f, c)] || []).filter((e) => !e.closed).reduce((a, e) => a + (e.pending_qty || 0), 0);

  // Base low-stock rows (full dataset, before column filters / search).
  const baseRows = useMemo(() => (stock || [])
    .filter((i) => i.active !== false && (i.available_kg ?? 0) <= threshold)
    .sort((a, b) => (a.available_kg ?? 0) - (b.available_kg ?? 0)), [stock, threshold]);

  // Search across fabric, colour, and any remark in the row's vendor calls.
  const searchMatch = (r) => {
    if (!query.trim()) return true;
    if (itemMatchesQuery(r, query, codeMap)) return true;
    const q = norm(query);
    return (callsByKey[keyOf(r.fabric, r.color)] || []).some((e) => norm(e.remark).includes(q));
  };

  const colValues = (accessor) => Array.from(new Set(baseRows.map(accessor))).sort((a, b) => String(a).localeCompare(String(b), undefined, { numeric: true }));
  const passesColFilters = (r) => {
    if (colFilters.fabric.size && !colFilters.fabric.has(r.fabric)) return false;
    if (colFilters.color.size && !colFilters.color.has(r.color)) return false;
    if (colFilters.available.size && !colFilters.available.has(String(r.available_kg ?? 0))) return false;
    if (colFilters.min.size && !colFilters.min.has(String(r.min_kg ?? 0))) return false;
    if (colFilters.pending.size && !colFilters.pending.has(String(totalPending(r.fabric, r.color)))) return false;
    return true;
  };
  const toggleColFilter = (col, v) => setColFilters((p) => { const n = new Set(p[col]); n.has(v) ? n.delete(v) : n.add(v); return { ...p, [col]: n }; });
  const clearColFilter = (col) => setColFilters((p) => ({ ...p, [col]: new Set() }));

  const rows = useMemo(() => baseRows.filter((r) => searchMatch(r) && passesColFilters(r)), [baseRows, query, colFilters, callsByKey]);

  const allSelected = rows.length > 0 && rows.every((r) => selected.has(keyOf(r.fabric, r.color)));
  const toggleSelectAll = () => {
    setSelected((prev) => {
      if (rows.every((r) => prev.has(keyOf(r.fabric, r.color)))) {
        const n = new Set(prev); rows.forEach((r) => n.delete(keyOf(r.fabric, r.color))); return n;
      }
      const n = new Set(prev); rows.forEach((r) => n.add(keyOf(r.fabric, r.color))); return n;
    });
  };
  const toggleSelect = (k) => setSelected((p) => { const n = new Set(p); n.has(k) ? n.delete(k) : n.add(k); return n; });

  // ----- Vendor call entry actions -----
  const setDraft = (k, patch) => setDrafts((p) => ({ ...p, [k]: { ...(p[k] || { qty: "", call_date: "", supplier_id: "", remark: "" }), ...patch } }));
  const addEntry = async (r) => {
    const k = keyOf(r.fabric, r.color);
    const d = drafts[k] || {};
    const qty = evalFormula(d.qty) ?? Number(d.qty) ?? 0;
    if (!qty || qty <= 0) return toast.error("Enter a quantity greater than 0");
    try {
      await addVendorCall(token, { fabric: r.fabric, color: r.color, supplier_id: d.supplier_id || "", qty_called: qty, call_date: d.call_date || "", remark: d.remark || "" });
      toast.success("Vendor call logged");
      setDrafts((p) => ({ ...p, [k]: { qty: "", call_date: "", supplier_id: "", remark: "" } }));
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const saveEntry = async (entry, patch) => {
    try { await updateVendorCall(token, entry.id, patch); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const removeEntry = async (entry) => {
    if (!window.confirm("Delete this vendor call entry? This cannot be undone.")) return;
    try { await deleteVendorCall(token, entry.id); toast.success("Entry deleted"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  // ----- Excel export ----- (one row per vendor call entry; 4 columns only)
  const buildExport = (exportRows) => {
    const header = ["Call Date", "Fabric Type", "Colour", "Required Qty"];
    const out = [header];
    exportRows.forEach((r) => {
      const u = unitOf(r.fabric);
      const entries = callsByKey[keyOf(r.fabric, r.color)] || [];
      entries.forEach((e) => {
        out.push([formatDate(e.call_date) || "", r.fabric, r.color, `${fmt(e.qty_called)} ${u}`]);
      });
    });
    return out;
  };
  const download = (which) => {
    const exportRows = which === "selected" ? rows.filter((r) => selected.has(keyOf(r.fabric, r.color))) : rows;
    if (exportRows.length === 0) return toast.error(which === "selected" ? "No rows selected" : "Nothing to export");
    const ws = XLSX.utils.aoa_to_sheet(buildExport(exportRows));
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Low Stock");
    XLSX.writeFile(wb, `low-stock-${which}-${new Date().toISOString().slice(0, 10)}.xlsx`);
  };

  return (
    <>
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <div>
          <h2 className="font-display font-black text-2xl tracking-tight text-white">Low Stock</h2>
          <p className="text-xs text-white/50 mt-0.5">Colours at or below the threshold. Click a row to log & track vendor calls. Updates live.</p>
        </div>
        <div className="flex items-center gap-2">
          <Label className="text-[10px] uppercase tracking-[0.2em] text-white/40">Threshold (kg)</Label>
          <Input type="number" min="0" step="0.5" value={threshold} onChange={(e) => setThreshold(Number(e.target.value) || 0)} data-testid="low-stock-threshold"
            className="rounded-none h-9 w-24 bg-[#141414] border-white/10 text-white text-sm focus-visible:ring-2 focus-visible:ring-white/30" />
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" data-testid="low-stock-download-btn" className="rounded-none h-9 border-white/20 bg-transparent text-white hover:bg-white hover:text-black text-xs uppercase tracking-wider transition-colors">
                <Download className="h-3.5 w-3.5 mr-2" /> Download
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="rounded-none bg-[#141414] border-white/15 text-white">
              <DropdownMenuItem data-testid="low-stock-download-selected" onClick={() => download("selected")} className="text-xs cursor-pointer focus:bg-white focus:text-black">Download Selected ({selected.size})</DropdownMenuItem>
              <DropdownMenuItem data-testid="low-stock-download-all" onClick={() => download("all")} className="text-xs cursor-pointer focus:bg-white focus:text-black">Download All ({rows.length})</DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      <div className="relative w-full sm:w-96 mb-4">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-white/30" />
        <Input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search fabric, colour or remarks…" data-testid="low-stock-search"
          className="rounded-none h-10 bg-[#141414] border-white/10 pl-10 pr-9 text-white text-sm" />
        {query && <button onClick={() => setQuery("")} data-testid="low-stock-search-clear" className="absolute right-2 top-1/2 -translate-y-1/2 h-6 w-6 flex items-center justify-center text-white/40 hover:text-white"><X className="h-4 w-4" /></button>}
      </div>

      <div className="border border-white/10 overflow-x-auto thin-scroll">
        <div className="min-w-[820px]">
          <div className="grid grid-cols-12 gap-3 px-4 py-3 border-b border-white/10 text-xs font-bold uppercase tracking-[0.15em] text-white/40 bg-[#141414] items-center">
            <div className="col-span-1 flex items-center">
              <input type="checkbox" checked={allSelected} onChange={toggleSelectAll} data-testid="low-stock-select-all" className="accent-white h-4 w-4" />
            </div>
            <div className="col-span-3"><ColumnFilter label="Fabric" values={colValues((r) => r.fabric)} selected={colFilters.fabric} onToggle={(v) => toggleColFilter("fabric", v)} onClear={() => clearColFilter("fabric")} /></div>
            <div className="col-span-3"><ColumnFilter label="Colour" values={colValues((r) => r.color)} selected={colFilters.color} onToggle={(v) => toggleColFilter("color", v)} onClear={() => clearColFilter("color")} /></div>
            <div className="col-span-2 justify-end flex"><ColumnFilter label="Current" align="right" values={colValues((r) => String(r.available_kg ?? 0))} selected={colFilters.available} onToggle={(v) => toggleColFilter("available", v)} onClear={() => clearColFilter("available")} /></div>
            <div className="col-span-1 justify-end flex"><ColumnFilter label="Min" align="right" values={colValues((r) => String(r.min_kg ?? 0))} selected={colFilters.min} onToggle={(v) => toggleColFilter("min", v)} onClear={() => clearColFilter("min")} /></div>
            <div className="col-span-2 justify-end flex"><ColumnFilter label="Pending" align="right" values={colValues((r) => String(totalPending(r.fabric, r.color)))} selected={colFilters.pending} onToggle={(v) => toggleColFilter("pending", v)} onClear={() => clearColFilter("pending")} /></div>
          </div>

          {rows.length === 0 && (
            <div className="py-12 text-center text-white/40 text-sm" data-testid="no-low-stock">Nothing matches. All colours are above {threshold} kg or filtered out.</div>
          )}

          {rows.slice(0, limit).map((r) => {
            const k = keyOf(r.fabric, r.color);
            const tid = `${slug(r.fabric)}-${slug(r.color)}`;
            const u = unitOf(r.fabric);
            const qty = r.available_kg ?? 0;
            const critical = qty <= (r.min_kg ?? 0);
            const isOpen = expanded === k;
            const entries = callsByKey[k] || [];
            const tp = totalPending(r.fabric, r.color);
            const d = drafts[k] || { qty: "", call_date: "", supplier_id: "", remark: "" };
            return (
              <div key={k} className="border-b border-white/10" data-testid={`low-stock-row-${tid}`}>
                <div className="grid grid-cols-12 gap-3 px-4 py-2.5 text-sm items-center hover:bg-white/[0.02] transition-colors">
                  <div className="col-span-1 flex items-center gap-1.5">
                    <input type="checkbox" checked={selected.has(k)} onChange={() => toggleSelect(k)} data-testid={`low-stock-select-${tid}`} className="accent-white h-4 w-4" onClick={(e) => e.stopPropagation()} />
                    <button onClick={() => setExpanded(isOpen ? null : k)} data-testid={`low-stock-expand-${tid}`} className="text-white/40 hover:text-white transition-colors">
                      {isOpen ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                    </button>
                  </div>
                  <button onClick={() => setExpanded(isOpen ? null : k)} className="col-span-3 text-left text-white truncate">{r.fabric}</button>
                  <div className="col-span-3 flex items-center gap-2 min-w-0">
                    <span className="h-5 w-5 border border-white/20 shrink-0" style={{ backgroundColor: (colorByName[r.color] || {}).hex || SWATCHES[r.color] || "#888" }} />
                    <span className="text-white/80 truncate">{r.color}</span>
                  </div>
                  <div className={`col-span-2 text-right tabular font-bold ${critical ? "text-red-400" : "text-amber-400"}`} data-testid={`low-stock-available-${tid}`}>{fmt(qty)} {u}</div>
                  <div className="col-span-1 text-right tabular text-white/50">{fmt(r.min_kg ?? 0)}</div>
                  <div className="col-span-2 text-right tabular text-white" data-testid={`low-stock-pending-${tid}`}>{tp > 0 ? <span className="text-sky-400 font-bold">{fmt(tp)} {u}</span> : <span className="text-white/30">—</span>}</div>
                </div>

                {isOpen && (
                  <div className="px-4 pb-4 pt-1 bg-[#0B0B0B]" data-testid={`vendor-calls-${tid}`}>
                    <div className="text-[10px] uppercase tracking-[0.2em] text-white/40 mb-2 flex items-center gap-2"><PackageCheck className="h-3.5 w-3.5" /> Vendor Call Log · {r.fabric} · {r.color}</div>

                    {/* Add new entry */}
                    <div className="grid grid-cols-12 gap-2 items-center mb-3 p-2 border border-white/10 bg-[#111]">
                      <div className="col-span-2">
                        <Label className="text-[9px] uppercase tracking-wider text-white/30 block mb-0.5">Qty ({u})</Label>
                        <FormulaInput value={d.qty} onValueChange={(v) => setDraft(k, { qty: v })} placeholder="e.g. 50+25" data-testid={`vc-new-qty-${tid}`} className="rounded-none h-8 bg-[#141414] border-white/10 text-white text-sm focus-visible:ring-2 focus-visible:ring-white/30" />
                      </div>
                      <div className="col-span-2">
                        <Label className="text-[9px] uppercase tracking-wider text-white/30 block mb-0.5">Call Date</Label>
                        <Input type="date" value={d.call_date || ""} onChange={(e) => setDraft(k, { call_date: e.target.value })} data-testid={`vc-new-date-${tid}`} className="rounded-none h-8 bg-[#141414] border-white/10 text-white text-xs focus-visible:ring-2 focus-visible:ring-white/30" />
                      </div>
                      <div className="col-span-3">
                        <Label className="text-[9px] uppercase tracking-wider text-white/30 block mb-0.5">Vendor</Label>
                        <Select value={d.supplier_id || ""} onValueChange={(v) => setDraft(k, { supplier_id: v })}>
                          <SelectTrigger data-testid={`vc-new-vendor-${tid}`} className="h-8 rounded-none bg-[#141414] border-white/10 text-white text-xs focus:ring-2 focus:ring-white/30"><SelectValue placeholder="Select vendor" /></SelectTrigger>
                          <SelectContent className="rounded-none bg-[#141414] border-white/10 text-white">
                            {suppliers.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="col-span-4 flex items-end gap-2">
                        <div className="flex-1">
                          <Label className="text-[9px] uppercase tracking-wider text-white/30 block mb-0.5">Remarks</Label>
                          <Input value={d.remark || ""} onChange={(e) => setDraft(k, { remark: e.target.value })} placeholder="Notes / calc…" data-testid={`vc-new-remark-${tid}`} className="rounded-none h-8 bg-[#141414] border-white/10 text-white text-sm focus-visible:ring-2 focus-visible:ring-white/30" />
                        </div>
                        <Button onClick={() => addEntry(r)} data-testid={`vc-add-${tid}`} className="rounded-none h-8 bg-white text-black hover:bg-white/90 text-xs uppercase transition-colors"><Plus className="h-3.5 w-3.5 mr-1" /> Add</Button>
                      </div>
                    </div>

                    {/* Existing entries */}
                    {entries.length === 0 ? (
                      <div className="text-xs text-white/30 py-2">No vendor calls logged yet for this item.</div>
                    ) : (
                      <div className="border border-white/10">
                        <div className="grid grid-cols-12 gap-2 px-2 py-2 border-b border-white/10 text-[10px] font-bold uppercase tracking-wider text-white/40 bg-[#141414]">
                          <div className="col-span-2">Qty Called</div>
                          <div className="col-span-2">Call Date</div>
                          <div className="col-span-3">Vendor</div>
                          <div className="col-span-3">Remarks</div>
                          <div className="col-span-1 text-right">Pending</div>
                          <div className="col-span-1 text-right">·</div>
                        </div>
                        {entries.map((e) => (
                          <div key={e.id} className={`grid grid-cols-12 gap-2 px-2 py-2 border-b border-white/5 items-center ${e.closed ? "opacity-50" : ""}`} data-testid={`vc-entry-${e.id}`}>
                            <div className="col-span-2">
                              <FormulaInput value={String(e.qty_called)} onValueChange={(v) => { const num = evalFormula(v); if (num !== null && num !== e.qty_called) saveEntry(e, { qty_called: num }); }} data-testid={`vc-qty-${e.id}`} className="rounded-none h-8 bg-[#141414] border-white/10 text-white text-sm focus-visible:ring-2 focus-visible:ring-white/30" />
                            </div>
                            <div className="col-span-2">
                              <Input type="date" value={e.call_date || ""} onChange={(ev) => saveEntry(e, { call_date: ev.target.value })} data-testid={`vc-date-${e.id}`} className="rounded-none h-8 bg-[#141414] border-white/10 text-white text-xs focus-visible:ring-2 focus-visible:ring-white/30" />
                            </div>
                            <div className="col-span-3">
                              <Select value={e.supplier_id || ""} onValueChange={(v) => saveEntry(e, { supplier_id: v })}>
                                <SelectTrigger data-testid={`vc-vendor-${e.id}`} className="h-8 rounded-none bg-[#141414] border-white/10 text-white text-xs focus:ring-2 focus:ring-white/30"><SelectValue placeholder="—" /></SelectTrigger>
                                <SelectContent className="rounded-none bg-[#141414] border-white/10 text-white">
                                  {suppliers.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
                                </SelectContent>
                              </Select>
                            </div>
                            <div className="col-span-3">
                              <Input defaultValue={e.remark || ""} onBlur={(ev) => { if (ev.target.value !== (e.remark || "")) saveEntry(e, { remark: ev.target.value }); }} placeholder="—" data-testid={`vc-remark-${e.id}`} className="rounded-none h-8 bg-[#141414] border-white/10 text-white text-sm focus-visible:ring-2 focus-visible:ring-white/30" />
                            </div>
                            <div className="col-span-1 text-right tabular text-sm" data-testid={`vc-pending-${e.id}`}>
                              {e.closed ? <span className="text-emerald-400 text-[10px] uppercase">Done</span> : <span className="text-sky-400 font-bold">{fmt(e.pending_qty)}</span>}
                            </div>
                            <div className="col-span-1 flex justify-end">
                              <button onClick={() => removeEntry(e)} data-testid={`vc-delete-${e.id}`} className="p-1 text-red-400/60 hover:text-red-400 transition-colors" title="Delete entry"><Trash2 className="h-3.5 w-3.5" /></button>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}

          {rows.length > limit && (
            <div className="flex justify-center py-4">
              <Button variant="outline" onClick={() => setLimit((l) => l + 20)} data-testid="low-stock-load-more" className="rounded-none h-10 px-8 border-white/20 bg-transparent text-white hover:bg-white hover:text-black text-xs uppercase tracking-wider transition-colors">
                Load More ({rows.length - limit} more)
              </Button>
            </div>
          )}
        </div>
      </div>
    </>
  );
}
